import { createContext, useContext, useEffect, useState } from "react";
import { AxiosError } from "axios";
import { client } from "api";
import { logOut, selectUser, setToken, validateToken } from "./authSlice";
import { useAppDispatch, useAppSelector } from "app/hooks";
import LoadingPage from "pages/loading/LoadingPage";
import { User, UserRole } from "./types/user";

type AuthContextType = {
  user: User | null;
  hasRole: (roles: UserRole[]) => boolean;
};

const AuthContext = createContext<AuthContextType>({
  user: null,
  hasRole: () => false,
});

const useAuthContext = () => {
  return useContext(AuthContext);
};

const AuthProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectUser);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Add the token to every request if we have it
    const request = client.interceptors.request.use((config) => {
      const token = localStorage.getItem("token");
      return { ...config, headers: { ...config.headers, token } };
    });
    // Listen for status 401 from API,
    // meaning token is missing or invalid and user needs to log in
    const response = client.interceptors.response.use(undefined, (error: AxiosError) => {
      if (error.response?.status === 401) {
        dispatch(logOut());
      }
      return Promise.reject(error);
    });

    // Clean up
    return () => {
      client.interceptors.request.eject(request);
      client.interceptors.response.eject(response);
    };
  }, [dispatch]);

  // Try to auto login on startup
  useEffect(() => {
    const token = localStorage.getItem("token") ?? "";

    dispatch(validateToken(token))
      .unwrap()
      .then(() => dispatch(setToken(token)))
      .catch(() => dispatch(logOut()))
      .finally(() => setIsLoading(false));
  }, [dispatch]);

  function hasRole(roles: UserRole[]) {
    return roles.includes(user?.role ?? UserRole.NONE);
  }

  if (isLoading) return <LoadingPage />;
  return <AuthContext.Provider value={{ user, hasRole }}>{children}</AuthContext.Provider>;
};

export { AuthProvider as default, useAuthContext };
